import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowRight, Package, Warehouse, BarChart3, MapPin, Smartphone, Radio, Cpu, QrCode } from 'lucide-react';
import Testimonials from '../components/Testimonials';
import CaseStudies from '../components/CaseStudies';
import FAQ from '../components/FAQ';
import IndustriesSection from '../components/IndustriesSection';

const Home = () => {
  const services = [
    {
      icon: <Warehouse className="w-12 h-12" />,
      title: 'WMS',
      description: 'Complete Warehouse Management System for optimized operations',
      link: '/services/wms'
    },
    {
      icon: <BarChart3 className="w-12 h-12" />,
      title: 'WIP Tracking',
      description: 'Real-time Work in Progress monitoring and management',
      link: '/services/wip'
    },
    {
      icon: <Package className="w-12 h-12" />,
      title: 'Inventory Tracking',
      description: 'Advanced inventory management with real-time visibility',
      link: '/services/inventory'
    },
    {
      icon: <MapPin className="w-12 h-12" />,
      title: 'Asset Tracking',
      description: 'Track and manage your assets with precision',
      link: '/services/asset-tracking'
    }
  ];

  const solutions = [
    {
      icon: <Smartphone className="w-10 h-10" />,
      title: 'Mobile Applications',
      description: 'Custom mobile apps for on-the-go management'
    },
    {
      icon: <QrCode className="w-10 h-10" />,
      title: 'Barcode Scanning',
      description: 'Efficient barcode scanning solutions'
    },
    {
      icon: <Cpu className="w-10 h-10" />,
      title: 'IoT Integration',
      description: 'Connected devices for smart operations'
    },
    {
      icon: <Radio className="w-10 h-10" />,
      title: 'RFID Technology',
      description: 'Advanced RFID tracking systems'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 px-4 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 opacity-95"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMzYjgyZjYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djItaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bS0yIDB2Mmgydi0yaC0yem0wIDR2Mmgydi0yaC0yem0wIDR2Mmgydi0yaC0yem0wIDR2Mmgydi0yaC0yem0wIDR2Mmgydi0yaC0yem0wIDR2Mmgydi0yaC0yem0wIDR2Mmgydi0yaC0yem0wIDR2Mmgydi0yaC0yem0wIDR2Mmgydi0yaC0yeiIvPjwvZz48L2c+PC9zdmc+')] opacity-30"></div>
        
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Transform Your <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Supply Chain</span> Operations
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 mb-8 leading-relaxed">
              Advanced WMS, Inventory Tracking, and Asset Management Solutions powered by IoT, RFID, and Mobile Technology
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/contact">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg transition-all duration-300 hover:scale-105">
                  Request Demo <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/services">
                <Button size="lg" variant="outline" className="border-2 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white px-8 py-6 text-lg transition-all duration-300">
                  Explore Services
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-4">Our Core Services</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">Comprehensive supply chain solutions tailored to your business needs</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="group hover:shadow-2xl transition-all duration-300 border-0 hover:-translate-y-2">
                <CardHeader>
                  <div className="text-blue-600 mb-4 group-hover:scale-110 transition-transform duration-300">
                    {service.icon}
                  </div>
                  <CardTitle className="text-2xl mb-2">{service.title}</CardTitle>
                  <CardDescription className="text-base">{service.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Link to={service.link} className="text-blue-600 font-semibold hover:text-blue-700 inline-flex items-center group">
                    Learn More <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-4">Technology Solutions</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">Cutting-edge technology for modern supply chain challenges</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {solutions.map((solution, index) => (
              <div key={index} className="group p-6 rounded-lg border-2 border-slate-200 hover:border-blue-400 transition-all duration-300 hover:shadow-lg">
                <div className="text-blue-600 mb-4 group-hover:scale-110 transition-transform duration-300">
                  {solution.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{solution.title}</h3>
                <p className="text-slate-600">{solution.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">Why Choose Sight Vision</h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">Industry-leading solutions backed by expertise and innovation</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-5xl font-bold text-blue-400 mb-4">10+</div>
              <h3 className="text-2xl font-semibold mb-2">Years Experience</h3>
              <p className="text-slate-300">Proven track record in supply chain solutions</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-cyan-400 mb-4">50+</div>
              <h3 className="text-2xl font-semibold mb-2">Projects Delivered</h3>
              <p className="text-slate-300">Successful implementations across industries</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-blue-400 mb-4">24/7</div>
              <h3 className="text-2xl font-semibold mb-2">Support Available</h3>
              <p className="text-slate-300">Round-the-clock technical assistance</p>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <IndustriesSection />

      {/* Case Studies Section */}
      <CaseStudies />

      {/* Testimonials Section */}
      <Testimonials />

      {/* FAQ Section */}
      <FAQ />

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">Ready to Transform Your Operations?</h2>
          <p className="text-xl text-slate-300 mb-8">Get in touch with our team to discuss how we can help your business</p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-slate-100 px-10 py-6 text-lg transition-all duration-300 hover:scale-105">
              Contact Us Today <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;